// Authentication utility functions
import { notifySeatLocked } from './seatLock';
import { notifyAccountArchived } from './archivedAccount';
import { notifyWorkspaceArchived } from './archivedWorkspace';

/**
 * Get the authentication token from localStorage
 * @returns {string|null} The token or null if not found
 */
export const getToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        return null;
    }
    return token;
};

/**
 * Set the authentication token in localStorage
 * @param {string} token - The JWT token to store
 */
export const setToken = (token) => {
    if (!token) {
        console.warn('Attempting to set null/undefined token');
        return;
    }
    localStorage.setItem('token', token);
};

/**
 * Remove the authentication token from localStorage
 */
export const removeToken = () => {
    console.log('Removing token from localStorage');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    // Also clear the remembered workspace. AuthContext restores currentWorkspace from
    // localStorage on login and prioritises it over the server's list, so leaving it
    // behind means the next sign-in resumes the previous session's workspace — for a
    // DIFFERENT user on a shared browser, and, for a seat-locked member, straight back
    // into the workspace that locked them, with no way out but devtools.
    localStorage.removeItem('currentWorkspace');
};

/**
 * Check if the user is authenticated
 * @returns {boolean} True if the user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
    const token = getToken();
    return !!token;
};

/**
 * Configure axios to include the token in all requests
 * @param {import('axios').AxiosInstance} axiosInstance - The axios instance to configure
 */
export const configureAxiosAuth = (axiosInstance) => {
    axiosInstance.interceptors.request.use(
        config => {
            const token = getToken();
            if (token) {
                // Ensure the token is properly formatted
                const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
                config.headers.Authorization = formattedToken;
            } else {
                console.warn('No token found for request to:', config.url);
            }
            return config;
        },
        error => {
            console.error('Request interceptor error:', error);
            return Promise.reject(error);
        }
    );

    // Add response interceptor to handle token expiration
    axiosInstance.interceptors.response.use(
        response => response,
        error => {
            const isLoginRequest = error.config?.url?.includes('/login');
            const alreadyOnLoginPage = typeof window !== 'undefined' &&
                                        window.location?.pathname === '/login';
            if (error.response?.status === 401 && !isLoginRequest && !alreadyOnLoginPage) {
                console.log('Auth failed (401), logging out:', error.response?.data?.error);
                removeToken();
                window.location.href = '/login';
            } else if (error.response?.status === 401 && alreadyOnLoginPage) {
                removeToken();
            } else if (
                error.response?.status === 403 &&
                error.response?.data?.error === 'account_read_only'
            ) {
                // A write was attempted on a locked (trial-expired / out-of-credits)
                // account. Read-only is view-only, so bounce the write to billing —
                // unless we're already on the billing page.
                const onBilling = typeof window !== 'undefined' &&
                                  window.location?.pathname?.startsWith('/settings/billing');
                if (!onBilling) {
                    window.location.href = '/settings/billing';
                }
            } else if (
                error.response?.status === 403 &&
                error.response?.data?.error === 'feature_unavailable'
            ) {
                // The feature isn't on the current plan. Only bounce to billing for
                // user-initiated writes (POST/PUT/PATCH/DELETE). Background GET reads —
                // e.g. dashboard stat fetches that hit gated endpoints — must reject
                // silently; otherwise a single gated read hijacks the whole app to
                // billing and traps a user who still has access to other features.
                // Nav is already hidden via useFeature, so gated reads are non-fatal.
                const method = (error.config?.method || 'get').toLowerCase();
                const onBilling = typeof window !== 'undefined' &&
                                  window.location?.pathname?.startsWith('/settings/billing');
                if (method !== 'get' && !onBilling) {
                    // The backend always sends upgrade_url: "/settings/billing" with no
                    // query string, so appending ?upgrade=1 client-side is what actually
                    // opens the upgrade modal — relying on a fallback here would never fire.
                    const base = error.response?.data?.upgrade_url || '/settings/billing';
                    window.location.href = base.includes('?') ? `${base}&upgrade=1` : `${base}?upgrade=1`;
                }
            } else if (
                error.response?.status === 403 &&
                error.response?.data?.error === 'seat_limit_exceeded'
            ) {
                // The workspace downgraded to a plan with fewer seats than it has
                // members (e.g. Growth -> pay-as-you-go); only the owner keeps
                // access, so this member 403s on reads as well as writes —
                // essentially every request they make. A per-request redirect
                // (account_read_only / feature_unavailable above) is wrong here:
                // it would fire on every page load, and there's nowhere useful to
                // send them — they aren't the owner, so billing 403s just the
                // same. Signal the app instead so it can show a persistent
                // full-screen explanation (see SeatLockGate).
                notifySeatLocked(error.response.data);
            } else if (
                error.response?.status === 410 &&
                error.response?.data?.error === 'account_archived'
            ) {
                // The workspace was archived while this session was open. The
                // token stays valid for up to 24h (jwt_blacklists has no
                // user_id, so it can't be revoked per-user), and every request
                // 410s from here on. Signal the app for a persistent
                // full-screen explanation — same reasoning as seat_limit_exceeded
                // above, see src/utils/archivedAccount.js.
                notifyAccountArchived(error.response.data);
            } else if (
                error.response?.status === 410 &&
                error.response?.data?.error === 'workspace_archived'
            ) {
                // Only this workspace was archived; the account is live. Move the
                // user on (WorkspaceArchivedListener) instead of locking the app.
                notifyWorkspaceArchived(error.response.data);
            }
            return Promise.reject(error);
        }
    );
}; 